/**
 * InputSystem — Action mapping, context stack, device polling.
 *
 * Runs in Phase 1 (Input). Double-buffers events, polls gamepads,
 * resolves bindings through the context stack, and freezes snapshot.
 */

import type { ISystem } from '../ecs/System.js';
import type { World } from '../ecs/World.js';
import { LoopPhase, SubPhase } from '../shared/types.js';
import { Logger } from '../core/Logger.js';
import { InputReader } from './InputReader.js';
import { GamepadManager } from './GamepadManager.js';
import { TouchProvider } from './TouchProvider.js';
import { HapticsManager } from './HapticsManager.js';
import { DeviceDetector } from './DeviceDetector.js';
import type { EventBus } from '../core/EventBus.js';
import type { RebindManager } from './Rebinding.js';

// ─── Action Types ────────────────────────────────────────────────────────────

export const enum ActionType {
  Button,
  Axis1D,
  Axis2D,
  Trigger,
}

export interface ActionBinding {
  action: string;
  type: ActionType;
  /** @internal Cached edge-detection key (action + source) — rebuilt when the binding is patched (2026-09-12). */
  _key?: string;
  // Keyboard bindings (event.code)
  keys?: string[];
  // Composite axis (positive/negative keys)
  positiveKey?: string;
  negativeKey?: string;
  // Gamepad
  gamepadButton?: number;
  gamepadAxis?: number;
  gamepadAxisPositive?: number;
  gamepadAxisNegative?: number;
  // Mouse
  mouseButton?: number;
}

export interface InputContext {
  name: string;
  priority: number;
  bindings: ActionBinding[];
  /**
   * Prevent lower-priority contexts from seeing consumed actions.
   * NOTE: Only affects keyboard/mouse bindings resolved through the context stack.
   * Gamepad buttons and touch gestures are written directly by their providers
   * before context resolution runs, so they bypass consumption.
   */
  consume?: boolean;
  enabled: boolean;
}

export { InputReader };

// ─── InputSystem ─────────────────────────────────────────────────────────────

export class InputSystem implements ISystem {
  readonly name = 'Input';
  readonly phase = LoopPhase.Input;
  readonly subPhase = SubPhase.Update;
  readonly priority = 0;

  readonly reader = new InputReader();
  readonly gamepadManager = new GamepadManager();
  readonly touchProvider = new TouchProvider();
  readonly haptics = new HapticsManager();
  readonly deviceDetector = new DeviceDetector();

  private contexts: InputContext[] = [];
  // DOM event handlers mutate these Sets directly instead of pushing event
  // objects into intermediate buffers. Handlers are single-threaded and
  // can only fire between ticks, so direct Set mutation is safe and
  // removes ~60-144 per-event allocations/sec on active keyboard/mouse.
  private keysDown = new Set<string>();
  private mouseButtonsDown = new Set<number>();
  // Accumulated pointer/scroll deltas since the last execute(). Scalars
  // instead of an array of MouseMoveEvent objects.
  private _pendingMovX = 0;
  private _pendingMovY = 0;
  private _pendingScroll = 0;
  private _lastPointerX = 0;
  private _lastPointerY = 0;
  private _pointerDirty = false;
  // Reusable Set for per-frame action-consumption. Previously
  // `new Set<string>()` was allocated every execute() — at 60-90 Hz that
  // was a small but completely avoidable per-frame allocation.
  private _consumed = new Set<string>();
  /**
   * Edge-detection for keyboard/mouse bindings. Compound keys encode
   * "action\0sourceId" so each binding's contribution is tracked individually.
   * Only Set add/delete transitions modify the per-action ref count in
   * `_kbMouseCounts`, preventing inflation from repeated held frames.
   */
  private _kbMouseSources = new Set<string>();
  /** Per-action count of active keyboard/mouse binding sources. */
  private _kbMouseCounts = new Map<string, number>();

  private _boundKeyDown: (e: KeyboardEvent) => void;
  private _boundKeyUp: (e: KeyboardEvent) => void;
  private _boundMouseMove: (e: MouseEvent) => void;
  private _boundMouseDown: (e: MouseEvent) => void;
  private _boundMouseUp: (e: MouseEvent) => void;
  private _boundWheel: (e: WheelEvent) => void;
  private _boundBlur: () => void;
  private _boundVisibilityChange: () => void;

  private canvas: HTMLCanvasElement | null = null;
  private _globalListenersAttached = false;
  private _unsubRebind: (() => void) | null = null;

  constructor(options?: { events?: EventBus; rebindManager?: RebindManager }) {
    this._boundKeyDown = (e) => this.onKeyDown(e);
    this._boundKeyUp = (e) => this.onKeyUp(e);
    this._boundMouseMove = (e) => this.onMouseMove(e);
    this._boundMouseDown = (e) => this.onMouseDown(e);
    this._boundMouseUp = (e) => this.onMouseUp(e);
    this._boundWheel = (e) => this.onWheel(e);
    this._boundBlur = () => this.onBlur();
    this._boundVisibilityChange = () => {
      if (document.hidden) this.onBlur();
    };

    if (options?.events) this.setEventBus(options.events);
    if (options?.rebindManager) this.setRebindManager(options.rebindManager);
  }

  /**
   * Attach an EventBus. DeviceDetector change events and rebind changes
   * fire through it. Safe to call multiple times — replaces the prior bus.
   */
  setEventBus(events: EventBus): void {
    // DeviceDetector was documented to fire through EventBus but was never
    // wired. Hook it here so UI code can react to device changes (e.g.
    // switching between keyboard glyphs and gamepad glyphs).
    this.deviceDetector.onChange = (prev, curr) => {
      events.emit('input:device-changed' as any, { previous: prev, current: curr });
    };
  }

  /**
   * Attach a RebindManager whose bindings should feed into the live
   * InputContexts. Without this wire-up, runtime rebinds only took effect
   * after a page reload because resolveButton reads from ctx.bindings —
   * which was frozen at pushContext() time.
   */
  setRebindManager(manager: RebindManager): void {
    this._unsubRebind?.();
    this._unsubRebind = manager.onBindingChanged((action, binding) => {
      this._patchLiveBinding(action, binding);
    });
  }

  /**
   * Replace the live ActionBinding for `action` across all contexts with
   * the freshly-resolved binding. Called from the RebindManager listener
   * so rebinds take effect on the very next tick.
   */
  private _patchLiveBinding(action: string, binding: { keys?: string[]; gamepadButton?: number; mouseButton?: number; positiveKey?: string; negativeKey?: string } | undefined): void {
    if (!binding) return;
    for (const ctx of this.contexts) {
      for (let i = 0; i < ctx.bindings.length; i++) {
        if (ctx.bindings[i].action === action) {
          // Preserve the original action + type fields; splice in the
          // rebindable inputs from the new resolved binding.
          const prev = ctx.bindings[i];
          // Evict the OLD binding's edge-detection source before we
          // overwrite it. resolveButton keys edge detection on
          // compoundKey = action+'\0'+sourceId derived from keys/mouseButton;
          // if the old key is physically held through a rebind to a
          // different key, the old compoundKey would otherwise linger in
          // _kbMouseSources with _kbMouseCounts[action]>=1 forever (later
          // frames only visit the NEW compoundKey), so _setReleased never
          // fires and the action stays stuck in reader.held. Mirror
          // _evictContextSources's per-binding logic here.
          const oldSourceId = (prev.keys?.join('+') ?? '')
            + (prev.mouseButton !== undefined ? `m${prev.mouseButton}` : '');
          const oldCompoundKey = action + '\0' + oldSourceId;
          if (this._kbMouseSources.has(oldCompoundKey)) {
            this._kbMouseSources.delete(oldCompoundKey);
            const count = this._kbMouseCounts.get(action) ?? 1;
            const next = count - 1;
            if (next <= 0) {
              this._kbMouseCounts.delete(action);
              this.reader._setReleased(action);
            } else {
              this._kbMouseCounts.set(action, next);
            }
          }
          ctx.bindings[i] = {
            ...prev,
            _key: undefined, // the cached edge key follows the OLD inputs
            keys: binding.keys ?? prev.keys,
            gamepadButton: binding.gamepadButton ?? prev.gamepadButton,
            mouseButton: binding.mouseButton ?? prev.mouseButton,
            positiveKey: binding.positiveKey ?? prev.positiveKey,
            negativeKey: binding.negativeKey ?? prev.negativeKey,
          };
        }
      }
    }
  }

  /**
   * Attach DOM event listeners to a canvas element.
   * Safe to call multiple times — previous canvas listeners are removed first.
   */
  attach(canvas: HTMLCanvasElement): void {
    // Remove canvas-bound listeners from any previous canvas to prevent leaks
    if (this.canvas && this.canvas !== canvas) {
      this.canvas.removeEventListener('mousemove', this._boundMouseMove);
      this.canvas.removeEventListener('mousedown', this._boundMouseDown);
      this.canvas.removeEventListener('mouseup', this._boundMouseUp);
      this.canvas.removeEventListener('wheel', this._boundWheel);
    }
    this.canvas = canvas;
    // Guard against double-attachment of document/window listeners on
    // hot-reload or repeated attach() calls; only canvas listeners are
    // re-attached per call (since canvas may change).
    if (!this._globalListenersAttached) {
      document.addEventListener('keydown', this._boundKeyDown);
      document.addEventListener('keyup', this._boundKeyUp);
      window.addEventListener('blur', this._boundBlur);
      document.addEventListener('visibilitychange', this._boundVisibilityChange);
      this._globalListenersAttached = true;
    }
    canvas.addEventListener('mousemove', this._boundMouseMove);
    canvas.addEventListener('mousedown', this._boundMouseDown);
    canvas.addEventListener('mouseup', this._boundMouseUp);
    canvas.addEventListener('wheel', this._boundWheel, { passive: false });
    this.touchProvider.attach(canvas);
    Logger.info('Input', 'Attached to canvas');
  }

  /**
   * Add an input context to the stack.
   */
  pushContext(context: InputContext): void {
    this.contexts.push(context);
    this.contexts.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Remove an input context.
   */
  removeContext(name: string): void {
    const idx = this.contexts.findIndex((c) => c.name === name);
    if (idx !== -1) {
      this._evictContextSources(this.contexts[idx]);
      this.contexts.splice(idx, 1);
    }
  }

  /**
   * Enable/disable a context by name.
   */
  setContextEnabled(name: string, enabled: boolean): void {
    const ctx = this.contexts.find((c) => c.name === name);
    if (!ctx) return;
    // When disabling an active context, evict any active sources so held
    // actions don't get permanently stuck (resolveButton won't run for them).
    if (!enabled && ctx.enabled) {
      this._evictContextSources(ctx);
    }
    ctx.enabled = enabled;
  }

  /**
   * Evict all active keyboard/mouse binding sources for a context.
   * Called when a context is disabled or removed so actions don't stay stuck
   * in the held state because resolveButton will no longer process them.
   */
  private _evictContextSources(ctx: InputContext): void {
    for (const binding of ctx.bindings) {
      const action = binding.action;
      const sourceId = (binding.keys?.join('+') ?? '')
        + (binding.mouseButton !== undefined ? `m${binding.mouseButton}` : '');
      const compoundKey = action + '\0' + sourceId;
      if (this._kbMouseSources.has(compoundKey)) {
        this._kbMouseSources.delete(compoundKey);
        const count = this._kbMouseCounts.get(action) ?? 1;
        const next = count - 1;
        if (next <= 0) {
          this._kbMouseCounts.delete(action);
          this.reader._setReleased(action);
        } else {
          this._kbMouseCounts.set(action, next);
        }
      }
    }
  }

  // ─── ISystem ─────────────────────────────────────────────────────

  execute(dt: number, _world: World): void {
    // 1. Clear previous frame's pressed/released
    this.reader._clearFrame();

    // 2. Apply accumulated pointer deltas.
    // Event handlers already mutated keysDown / mouseButtonsDown directly
    // so there's no buffer to drain for them.
    if (this._pointerDirty) {
      this.reader._setPointerPosition(this._lastPointerX, this._lastPointerY);
      this._pointerDirty = false;
    }
    this.reader._setPointerDelta(this._pendingMovX, this._pendingMovY);
    this._pendingMovX = 0;
    this._pendingMovY = 0;

    // 3. Apply accumulated scroll delta
    this.reader._setScrollDelta(this._pendingScroll);
    this._pendingScroll = 0;

    // 3.5. Touch provider poll
    this.touchProvider.poll(this.reader);

    // 4. Poll gamepads via GamepadManager
    this.gamepadManager.poll(this.reader, dt);

    // 4.5. Update haptics with gamepads already polled by GamepadManager (avoid double-poll + allocation)
    this.haptics.setGamepads(this.gamepadManager.getGamepadList());

    // 5. Resolve bindings through context stack. Reuse the same Set each
    // tick (clear + refill) rather than allocating a new one.
    this._consumed.clear();
    const consumed = this._consumed;
    for (const ctx of this.contexts) {
      if (!ctx.enabled) continue;
      for (const binding of ctx.bindings) {
        if (consumed.has(binding.action)) continue;

        this.resolveBinding(binding);

        if (ctx.consume) {
          consumed.add(binding.action);
        }
      }
    }

    // 7.5. Device detection
    const hasKB = this.keysDown.size > 0;
    const hasMouse = this.mouseButtonsDown.size > 0;
    const hasGP = this.gamepadManager.getConnectedCount() > 0;
    const hasTouch = this.touchProvider.getActivePointers().size > 0;
    this.deviceDetector.update({
      hasKeyboard: hasKB,
      hasMouse: hasMouse,
      hasGamepad: hasGP,
      hasTouch: hasTouch,
      hasXR: this.reader.isXRActive(),
      hasVisionPro: this.reader.isVisionProActive(),
    });
    this.reader._setActiveDevice(this.deviceDetector.getActiveDevice());
    this.reader._setTouchActive(hasTouch);

    // 8. Tick held durations
    this.reader._tickHeldDurations(dt);
  }

  private resolveBinding(binding: ActionBinding): void {
    switch (binding.type) {
      case ActionType.Button:
        this.resolveButton(binding);
        break;
      case ActionType.Axis1D:
        this.resolveAxis1D(binding);
        break;
      case ActionType.Axis2D:
        // Axis2D is handled by combining two Axis1D bindings externally
        break;
      case ActionType.Trigger:
        this.resolveButton(binding); // Same as button, pressed only
        break;
    }
  }

  private resolveButton(binding: ActionBinding): void {
    const action = binding.action;
    let down = false;

    // Check keyboard keys
    if (binding.keys) {
      for (const key of binding.keys) {
        if (this.keysDown.has(key)) { down = true; break; }
      }
    }

    // Check mouse buttons
    if (!down && binding.mouseButton !== undefined) {
      if (this.mouseButtonsDown.has(binding.mouseButton)) down = true;
    }

    // Gamepad button input is handled by GamepadManager writing directly to the reader
    // under its own namespace (GP_A, GP_B, GP_LeftStick, etc.). This resolver only
    // handles keyboard/mouse sources. The two namespaces must not overlap — if a context
    // binding uses a GP_* action name with keyboard keys, releasing the key will evict
    // the action even if the gamepad is still holding it. Use distinct action names
    // (e.g., "Jump") for keyboard bindings and consume GP_* names via gameplay code.

    // Build a stable compound key for this binding to enable edge detection.
    // Only transitions (not-active→active or active→not-active) modify the
    // ref count, preventing inflation from repeated held frames. Cached on
    // the binding: this ran per binding per tick (two or three strings each).
    let compoundKey = binding._key;
    if (compoundKey === undefined) {
      const sourceId = (binding.keys?.join('+') ?? '')
        + (binding.mouseButton !== undefined ? `m${binding.mouseButton}` : '');
      compoundKey = binding._key = action + '\0' + sourceId;
    }

    if (down) {
      if (!this._kbMouseSources.has(compoundKey)) {
        this._kbMouseSources.add(compoundKey);
        const prev = this._kbMouseCounts.get(action) ?? 0;
        this._kbMouseCounts.set(action, prev + 1);
        if (prev === 0 && !this.reader.isHeld(action)) {
          this.reader._setPressed(action);
        }
      }
    } else {
      if (this._kbMouseSources.has(compoundKey)) {
        this._kbMouseSources.delete(compoundKey);
        const count = this._kbMouseCounts.get(action) ?? 1;
        const next = count - 1;
        if (next <= 0) {
          this._kbMouseCounts.delete(action);
          this.reader._setReleased(action);
        } else {
          this._kbMouseCounts.set(action, next);
        }
      }
    }
  }

  private resolveAxis1D(binding: ActionBinding): void {
    const action = binding.action;
    let value = 0;
    let hasKbInput = false;

    // Composite keyboard axis
    if (binding.positiveKey && binding.negativeKey) {
      if (this.keysDown.has(binding.positiveKey)) { value += 1; hasKbInput = true; }
      if (this.keysDown.has(binding.negativeKey)) { value -= 1; hasKbInput = true; }
    }

    // Gamepad axis input is handled by GamepadManager writing directly to the reader.
    // Only write if keyboard actually contributes AND a gamepad hasn't already written
    // a non-zero value this frame (_clearFrame zeroes all axes, so non-zero = live input).
    if (hasKbInput) {
      const existing = this.reader.getAxis(action);
      if (existing === 0) {
        this.reader._setAxis(action, value);
      }
    }
  }

  // ─── DOM Event Handlers ──────────────────────────────────────────

  // Set of keys whose default browser action should be suppressed during
  // gameplay. Hoisted so the check is a Set.has (O(1)) instead of a fresh
  // Array.includes on every keydown.
  private static readonly _SUPPRESSED_KEYS = new Set([
    'Space', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  ]);

  private onKeyDown(e: KeyboardEvent): void {
    // Don't suppress browser shortcuts
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // Add BEFORE the repeat guard: after a focus loss clears keysDown, a key
    // still physically held only ever produces repeat events — dropping them
    // left the action dead until the player released and re-pressed.
    this.keysDown.add(e.code);
    if (e.repeat) return;
    if (InputSystem._SUPPRESSED_KEYS.has(e.code)) {
      e.preventDefault();
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.keysDown.delete(e.code);
  }

  private onMouseMove(e: MouseEvent): void {
    // Accumulate into scalars — no per-event object allocation. movementX
    // is relative; pointer position is absolute. Both are snapshotted into
    // the reader during execute().
    this._pendingMovX += e.movementX;
    this._pendingMovY += e.movementY;
    this._lastPointerX = e.clientX;
    this._lastPointerY = e.clientY;
    this._pointerDirty = true;
  }

  private onMouseDown(e: MouseEvent): void {
    this.mouseButtonsDown.add(e.button);
  }

  private onMouseUp(e: MouseEvent): void {
    this.mouseButtonsDown.delete(e.button);
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    this._pendingScroll += e.deltaY;
  }

  private onBlur(): void {
    // Focus loss invalidates all queued input. Clear in-flight accumulators
    // AND held Sets — resolveButton will then drive the keyboard/mouse
    // sources to false on the next execute(), releasing held actions
    // cleanly through the existing edge-detection code.
    this.keysDown.clear();
    this.mouseButtonsDown.clear();
    this._pendingMovX = 0;
    this._pendingMovY = 0;
    this._pendingScroll = 0;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────

  dispose(): void {
    // Split cleanup between canvas-bound and global listeners. Previously a
    // single `if (!this.canvas) return` gate let global listeners leak when
    // dispose was called after the canvas was already nulled externally or
    // when dispose ran twice during hot-reload.
    if (this._globalListenersAttached) {
      document.removeEventListener('keydown', this._boundKeyDown);
      document.removeEventListener('keyup', this._boundKeyUp);
      window.removeEventListener('blur', this._boundBlur);
      document.removeEventListener('visibilitychange', this._boundVisibilityChange);
      this._globalListenersAttached = false;
    }
    if (this.canvas) {
      this.canvas.removeEventListener('mousemove', this._boundMouseMove);
      this.canvas.removeEventListener('mousedown', this._boundMouseDown);
      this.canvas.removeEventListener('mouseup', this._boundMouseUp);
      this.canvas.removeEventListener('wheel', this._boundWheel);
      this.canvas = null;
    }
    this._unsubRebind?.();
    this._unsubRebind = null;
    // Clear all internal state so re-attach doesn't see stale data
    this._kbMouseSources.clear();
    this._kbMouseCounts.clear();
    this.keysDown.clear();
    this.mouseButtonsDown.clear();
    this._pendingMovX = 0;
    this._pendingMovY = 0;
    this._pendingScroll = 0;
    this._pointerDirty = false;
    this._consumed.clear();
    this.gamepadManager.dispose();
    this.touchProvider.dispose();
    this.haptics.dispose();
    this.deviceDetector.dispose();
    Logger.info('Input', 'Disposed');
  }
}
